import { PPDModal } from '../../../PPDModal';
import { useStore } from 'shared/hooks';
import { IUserDashboardEfficiencyItem, IUserDashboardLabels } from 'shared/models';
import { PPDWidgetGroupSelect } from '../PPDWidgetGroupSelect';
import { observer } from 'mobx-react';
import { useMemo } from 'react';

import classnames from 'classnames';
import { useDashboardDict } from 'pages/profile-dashboard/context/useDashboardDict';

import './styles.scss';
import { PPDWidgetEfficiencyTable } from 'pages/profile-dashboard/components/PPDWidget/components/PPDWidgetEfficiencyModal/components/PPDWidgetEfficiencyTable';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    items?: IUserDashboardEfficiencyItem[];
    labels?: IUserDashboardLabels;
    positionId?: string | number | null;
    onChangePosition?: (positionId: string | number) => void;
};

export type SortKey = "employee" | "store" | "value";
type SortOrder = "asc" | "desc";

export const PPDWidgetEffeciencyModal = observer(({
    isOpen,
    onClose,
    title,
    items = [],
    positionId = null,
    labels = {},
    onChangePosition = () => {}
}: Props) => {
    const { periods = [], employees = [], stores = [], positions = [] } = useDashboardDict() || {};

    const store = useStore(() => ({
        activePeriod: periods[0] || null,
        sortKey: null as SortKey | null,
        sortOrder: null as SortOrder | null,
    }));

    const handleSort = (key: SortKey) => {
        if (store.sortKey === key) {
            store.set('sortOrder', store.sortOrder === 'asc' ? "desc" : "asc");
        } else {
            store.set('sortKey', key);
            store.set('sortOrder', "desc");
        }
    };

    const sortedItems = useMemo(() => {
        if (!store.activePeriod) return [];

        let rows = items
            .filter(i => i.period_id === store.activePeriod?.id)
            .filter(i => {
                const emp = employees.find(e => e.id === i.employee_id);
                return emp?.position_id === positionId;
            })
            .map(i => {
                const emp = employees.find(e => e.id === i.employee_id);
                const storeName = stores.find(s => s.id === i.store_id)?.name || "—";

                return {
                    key: `${i.employee_id ?? 'employee'}-${i.store_id ?? 'store'}-${i.period_id ?? 'period'}`,
                    employee: emp?.name || "—",
                    store: storeName,
                    plan: i.plan ?? { amount: 0, unit_id: "currency" },
                    fact: i.fact ?? { amount: 0, unit_id: "currency" },
                    value: i.value ?? { amount: 0, unit_id: "percent" },
                };
            });

        if (store.sortKey) {
            rows = rows.sort((a, b) => {
                let compare = 0;
                if (store.sortKey === "employee") compare = a.employee.localeCompare(b.employee);
                if (store.sortKey === "store") compare = a.store.localeCompare(b.store);
                if (store.sortKey === "value") compare = (a.value?.amount || 0) - (b.value?.amount || 0);
                return store.sortOrder === "asc" ? compare : -compare;
            });
        }

        return rows;
    }, [items, employees, stores, positionId, store.activePeriod, store.sortKey, store.sortOrder]);

    const isFirstTabActive = store.activePeriod?.id === periods[0]?.id;

    return (
        <PPDModal isOpened={!!isOpen} onClose={onClose} className="ppd-widget-efficiency-modal">
            <div className="ppd-widget-efficiency-modal__header">
                {title && <div className="ppd-widget-stores-modal__title">{title}</div>}

                <PPDWidgetGroupSelect
                    items={positions}
                    value={positionId}
                    onChange={id => onChangePosition(id)}
                />
            </div>

            <div className="ppd-widget-stores-modal__content">
                <div className="ppd-widget-efficiency-modal__tabs">
                    {periods.map(p => {
                        const isItem = items.find(item => item.period_id === p.id);

                        if (isItem) {
                            return (
                                <button
                                    key={p.id}
                                    className={classnames("ppd-widget-efficiency-modal__tab", {
                                        "ppd-widget-efficiency-modal__tab--active": p.id === store.activePeriod?.id
                                    })}
                                    onClick={() => store.set("activePeriod", p)}
                                >
                                    {p.name || "—"}
                                </button>
                            )
                        }
                    })}
                </div>

                <div className="ppd-widget-efficiency-modal__table">
                    <PPDWidgetEfficiencyTable
                        items={sortedItems}
                        labels={labels}
                        sortKey={store.sortKey}
                        sortOrder={store.sortOrder}
                        onSort={handleSort}
                        noTopRadius={isFirstTabActive}
                    />
                </div>
            </div>
        </PPDModal>
    );
});
